<script setup lang="ts">
import {
  useLineActivationProviderSettingEditPageQuery,
  useUpdateLineActivationProviderSettingMutation,
} from './LineActivationProviderSettingEditPage.generated'
import LineActivationProviderSettingForm from '@/components/organisms/LineActivationProviderSetting/LineActivationProviderSettingForm.vue'
import { computed, ref } from 'vue'
import type { LineActivationProviderSettingFormType } from '@/components/organisms/LineActivationProviderSetting/formSchema'
import { useRouter } from 'vue-router'
import type { UpdateLineActivationProviderSettingInput } from '@/types/schema.generated'
import { useAlert } from '@/libs/alertPortal'

const alert = useAlert()
const router = useRouter()

const { result, onResult, refetch } =
  useLineActivationProviderSettingEditPageQuery({
    fetchPolicy: 'no-cache',
  })

onResult((result): void => {
  if (!result.data.lineActivationProviderSetting) {
    router.replace({ name: 'errorNotFound' })
    return
  }
})

const isSubmitting = ref(false)
const initialForm = computed<LineActivationProviderSettingFormType>(() => {
  return {
    messageApiAccessToken:
      result.value?.lineActivationProviderSetting?.messageApiAccessToken ?? '',
    messageApiChannelSecret:
      result.value?.lineActivationProviderSetting?.messageApiChannelSecret ??
      '',
    lineChannelId:
      typeof result.value?.lineActivationProviderSetting?.lineChannelId ===
      'string'
        ? result.value?.lineActivationProviderSetting?.lineChannelId
        : undefined,
    liffId:
      typeof result.value?.lineActivationProviderSetting?.liffId === 'string'
        ? result.value?.lineActivationProviderSetting?.liffId
        : undefined,
    lineFollowUrl:
      typeof result.value?.lineActivationProviderSetting?.lineFollowUrl ===
      'string'
        ? result.value?.lineActivationProviderSetting?.lineFollowUrl
        : undefined,
  }
})

const updateMutation = useUpdateLineActivationProviderSettingMutation()

const handleSubmit = async (
  input: UpdateLineActivationProviderSettingInput
) => {
  isSubmitting.value = true

  const res = await updateMutation.mutate({
    input,
  })
  if (!res) {
    isSubmitting.value = false
    alert.error('LINE連携設定の更新に失敗しました')
    return
  }

  if (res.errors) {
    isSubmitting.value = false
    alert.error('LINE連携設定の更新に失敗しました')
    return
  }

  if (!res.data) {
    isSubmitting.value = false
    alert.error('LINE連携設定の更新に失敗しました')
    return
  }

  isSubmitting.value = false
  alert.success('LINE連携設定を更新しました')

  refetch()
}
</script>

<template>
  <section :class="$style.wrapper" v-if="result?.lineActivationProviderSetting">
    <div :class="$style.container">
      <LineActivationProviderSettingForm
        :initialForm="initialForm"
        :isSubmitting="isSubmitting"
        @submit="handleSubmit"
      />
    </div>
  </section>
</template>

<style module lang="scss">
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 auto;
  width: 860px;
}

.container {
  background-color: $color-surface-primary;
  padding: 16px;
  border-radius: 10px;
}
</style>
